// Client composition root — the LIGHT half. Flow:
//   1. GET /api/auth/me -> logged out? show login/first-admin screen (lobby/)
//   2. logged in -> show lobby (world list + create)
//   3. pick world -> `await import('./game.js')` -> startWorld: ws JoinMsg,
//      WelcomeMsg, renderer, controls, HUD (all of it lives in game.ts)
//
// CODE SPLIT (task 20b): this entry module must NEVER statically import
// anything that reaches three/webgpu — no render, net, player, audio or world
// modules. The whole renderer (~950 kB minified) sits behind the ONE dynamic
// `import('./game.js')` at the lobby→world seam below, so the login and lobby
// screens load a few kilobytes and the heavy chunk downloads only when a
// world is actually joined. `main.test.mjs` pins this.
//
// M1 (task 6): steps 1–2 — auth gate + an admin users panel.
// M6 (task 18b): the lobby is a WORLD PICKER — the live list from
// GET /api/worlds (polled while visible, rows mutated in place, never rebuilt)
// plus a "Lume nouă" create form; the picked id rides into `JoinMsg.worldId`.
// Everything past the pick — the renderer, controls, presence, edits, HUD —
// is `game.ts`'s job.

import { showMessage } from './ui/status-message.js'
import {
  mountBackupPanel,
  mountUsersPanel,
  mountWorldPicker,
  requireSession,
  type SessionUser
} from './lobby/lobby.js'

export interface HealthResponse {
  ok: boolean
}

export async function fetchHealth(): Promise<HealthResponse> {
  const res = await fetch('/api/health')
  if (!res.ok) throw new Error(`health check failed: ${res.status}`)
  return (await res.json()) as HealthResponse
}

/**
 * The lobby: who you are, the world picker (task 18b), and the admin panel.
 * Resolves with the id of the world the player picked — the picker polls
 * /api/worlds while this screen is up and stops the moment a row is clicked.
 */
function showLobby(host: HTMLElement, user: SessionUser): Promise<number> {
  host.replaceChildren()
  host.classList.remove('hidden')
  host.style.cssText = 'display:flex;flex-direction:column;align-items:center;gap:1.25rem;padding:2rem 0;width:100%;min-height:100vh;overflow-y:auto;'

  const card = document.createElement('section')
  card.style.cssText =
    'width:min(28rem,92vw);padding:2rem;border-radius:1.75rem;color:#fff;font-family:Fredoka,system-ui,sans-serif;' +
    'background:linear-gradient(160deg,#0ea5e9 0%,#6366f1 60%,#a21caf 100%);box-shadow:0 24px 60px rgba(0,0,0,0.45);' +
    'display:flex;flex-direction:column;gap:0.9rem;'

  const title = document.createElement('h1')
  title.style.cssText = 'margin:0;font-size:1.9rem;'
  title.textContent = `Salut, ${user.email}! 👋`

  const sub = document.createElement('p')
  sub.style.cssText = 'margin:0;opacity:0.92;'
  sub.textContent = user.isAdmin ? 'Ești administratorul lumii. 👑' : 'Gata de construit? 🧱'

  card.append(title, sub)
  host.append(card)
  // Admins get the per-row world management controls (rename / hide / restore
  // — Montessori soft-delete, nothing destroyed), the users panel and the
  // backup download (per-database, so one card — never a per-row control).
  const picked = mountWorldPicker(host, { admin: user.isAdmin })
  if (user.isAdmin) {
    mountUsersPanel(host)
    mountBackupPanel(host)
  }
  return picked
}

async function boot(): Promise<void> {
  const auth = document.getElementById('login')
  const lobby = document.getElementById('lobby')
  const game = document.getElementById('game')
  if (!auth || !lobby || !game) return

  const user = await requireSession(auth)
  auth.classList.add('hidden')
  auth.replaceChildren()

  const worldId = await showLobby(lobby, user)
  lobby.classList.add('hidden')
  lobby.replaceChildren()

  game.classList.remove('hidden')
  game.style.cssText = 'position:fixed;inset:0;'

  // The lobby→world seam (task 20b): the renderer chunk downloads HERE, once,
  // behind a visible note. A `typeof import(…)` is type-only and free; only
  // this awaited import() actually fetches the chunk.
  const loading = showMessage(game, 'Se încarcă lumea… ⏳')
  let world: typeof import('./game.js')
  try {
    world = await import('./game.js')
  } catch (err) {
    console.error('[voxelcraft] failed to load the world module', err)
    loading.remove()
    showMessage(game, 'Nu pot descărca lumea. Verifică internetul și reîncearcă. ❌')
    return
  }
  loading.remove()
  const { hasWebGPU, startWorld } = world

  if (!hasWebGPU()) {
    showMessage(game, 'Browserul nu are WebGPU — folosim WebGL2. 🙂')
  }

  try {
    await startWorld(game, user, worldId)
  } catch (err) {
    console.error('[voxelcraft] renderer failed to start', err)
    showMessage(game, 'Nu pot porni placa video. Încearcă alt browser. ❌')
    return
  }

  // The health probe SPEAKS ONLY ON BAD NEWS: a healthy server used to print a
  // developer's ✅ line into a child's HUD, forever, saying nothing a playing kid
  // could act on. Silence is the success state.
  try {
    const health = await fetchHealth()
    if (!health.ok) showMessage(game, 'Serverul se poartă ciudat. Dacă ceva nu merge, reîncarcă pagina. 🤔')
  } catch {
    showMessage(game, 'Serverul nu răspunde. Reîncarcă pagina. ❌')
  }
}

void boot()
